import axios from "@/utils/axios";
import { formatExpedientDate } from "@/utils/format";
import type { ExternalData } from "@sdt/render-form-engine";

const EXPEDIENT_TABLE_PREFIX = "expedient-field:";
const EXPEDIENT_SOURCE = "EXPEDIENT";

export const isExpedientTable = (table: string, source?: string | null) =>
  table.startsWith(EXPEDIENT_TABLE_PREFIX) || source === EXPEDIENT_SOURCE;

const toStr = (v: unknown): string =>
  typeof v === "string" || typeof v === "number" || typeof v === "boolean"
    ? String(v)
    : "";

export const getExpedientFieldId = (table: string) =>
  table.startsWith(EXPEDIENT_TABLE_PREFIX)
    ? table.slice(EXPEDIENT_TABLE_PREFIX.length)
    : table;

// `field_name` -> target field type, for date fields autofilled from an
// expedient. Used to normalize only those field_values.
export type ExpedientDateFieldMap = Map<string, "date" | "datetime">;

// Normalize backend dates to the format date fields expect, only for
// field_values that map to a date field.
const normalizeExpedientFieldValues = (
  fieldValues: unknown,
  dateFields: ExpedientDateFieldMap,
): unknown => {
  if (!Array.isArray(fieldValues)) return fieldValues;
  return fieldValues.map((fv) => {
    if (
      !fv ||
      typeof fv !== "object" ||
      !("field_name" in fv) ||
      !("value" in fv)
    ) {
      return fv;
    }
    const entry = fv as { field_name?: unknown; value?: unknown };
    const fieldType =
      typeof entry.field_name === "string"
        ? dateFields.get(entry.field_name)
        : undefined;
    if (!fieldType || typeof entry.value !== "string") return fv;
    return {
      ...fv,
      value: formatExpedientDate(entry.value, fieldType === "datetime"),
    };
  });
};

export const mapExpedientItemToOption = (
  item: Record<string, unknown>,
  dateFields?: ExpedientDateFieldMap,
) => {
  const normalizedItem: Record<string, unknown> =
    dateFields?.size && Array.isArray(item.field_values)
      ? {
          ...item,
          field_values: normalizeExpedientFieldValues(
            item.field_values,
            dateFields,
          ),
        }
      : item;

  const id = toStr(normalizedItem.id);
  const name = toStr(normalizedItem.name);
  const uniqueCode = toStr(normalizedItem.unique_code);

  // Etiqueta para el selector: Código Único - Nombre o solo Nombre/Código si falta alguno
  const displayLabel =
    uniqueCode && name ? `${uniqueCode} - ${name}` : uniqueCode || name || id;

  return {
    ...normalizedItem,
    id,
    value: id,
    label: displayLabel,
  };
};

export const parseExpedientItems = (
  payload: unknown,
): Record<string, unknown>[] => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload && typeof payload === "object") {
    const obj = payload as { data?: unknown };
    if (Array.isArray(obj.data)) {
      return obj.data;
    }
    if (obj.data && typeof obj.data === "object") {
      const nested = obj.data as { data?: unknown };
      if (Array.isArray(nested.data)) {
        return nested.data;
      }
    }
  }
  return [];
};

export const fetchExpedientData = async (
  tableName: string,
  searchQuery: string,
  page: number,
  dateFields?: ExpedientDateFieldMap,
): Promise<ExternalData> => {
  const fieldId = getExpedientFieldId(tableName);
  const search = searchQuery.trim();

  if (!search) {
    return {
      current_page: 1,
      last_page: 1,
      data: [],
    };
  }

  const response = await axios.get(
    `/admin/v1/forms/expedient-fields/${fieldId}/search`,
    {
      params: {
        search,
        page,
        per_page: 10,
      },
    },
  );

  const payload = response.data;
  const items = parseExpedientItems(payload);

  return {
    current_page: payload?.meta?.current_page || 1,
    last_page: payload?.meta?.last_page || 1,
    data: items.map((item) => mapExpedientItemToOption(item, dateFields)),
  };
};

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : undefined;

// El meta del response lo tipa la librería; se deriva de ExternalData para no
// depender de que el tipo se exporte con un nombre en particular.
export type ResponseMeta = ExternalData extends { meta?: infer M } ? M : never;

const parseStandardTableResponse = (resPayload: unknown): ExternalData => {
  const payload = asRecord(resPayload);
  const tableData = payload?.data;
  const tableRecord = asRecord(tableData);

  let dataArray: ExternalData["data"] = [];
  if (Array.isArray(tableRecord?.data)) {
    dataArray = tableRecord.data;
  } else if (Array.isArray(tableData)) {
    dataArray = tableData;
  } else if (Array.isArray(payload?.data)) {
    dataArray = payload.data;
  }

  let current_page: number | undefined;
  if (typeof tableRecord?.current_page === "number") {
    current_page = tableRecord.current_page;
  } else if (typeof payload?.current_page === "number") {
    current_page = payload.current_page;
  }

  let last_page: number | undefined;
  if (typeof tableRecord?.last_page === "number") {
    last_page = tableRecord.last_page;
  } else if (typeof payload?.last_page === "number") {
    last_page = payload.last_page;
  }

  const isObject = Boolean(tableRecord) && !Array.isArray(tableData);
  const meta = (payload?.meta ||
    (isObject ? tableRecord?.meta : undefined)) as ResponseMeta;

  return {
    data: dataArray,
    ...(current_page !== undefined && { current_page }),
    ...(last_page !== undefined && { last_page }),
    ...(meta !== undefined && { meta }),
  };
};

export const fetchStandardData = async (
  tableName: string,
  source: string | undefined | null,
  normalizedFiltersParams: unknown[],
  search?: string,
  page: number = 1,
  column: string | null = null,
): Promise<ExternalData> => {
  const response = await axios.get("/admin/v1/data-center/tables/data", {
    params: {
      table_name: tableName,
      source,
      paginate: 1,
      filters: {
        ...normalizedFiltersParams,
      },
      filter: search ? { search } : {},
      order_by: column,
      page,
    },
  });

  return parseStandardTableResponse(response.data);
};
